import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class CreateRecordingRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.CreateRecording', 'CreateRecording');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    type CRArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'CreateRecording' }
    >;
    const a = args as CRArgs;
    this.checkUserId(a);
    this.checkTaskId(a);
    this.checkHardwareConfig(a, ctx);
  }

  /**
   * Check if the hardware config is valid.
   * @param args - The arguments of the step.
   * @param ctx - The validation context.
   */
  private checkHardwareConfig(
    args: Extract<RcStep.Arguments, { argumentKind: 'CreateRecording' }>,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    const { taskHardwareConfig, hardwareConfigEquipmentMapping } = args;
    const { equipment } = ctx;

    if (!taskHardwareConfig) {
      return;
    }

    const { config } = taskHardwareConfig;

    for (const [robotKey, robot] of Object.entries(config.robots)) {
      const mapping = hardwareConfigEquipmentMapping[robotKey];

      if (!mapping) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Equipment assignments missing for ${robotKey}`,
          fieldId: 'hardwareConfigEquipmentMapping',
        });
      }

      if (!robot.isPrimary) {
        if (!mapping.robotEquipmentId) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Equipment not assigned for ${robotKey}`,
            fieldId: 'hardwareConfigEquipmentMapping',
          });
        }

        if (
          equipment &&
          !equipment.find((e) => e.id === mapping.robotEquipmentId)
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Selected equipment for ${robotKey} is not available`,
            fieldId: 'hardwareConfigEquipmentMapping',
          });
        }
      }

      for (const [hardwareKey, hardwareConfig] of Object.entries(
        robot.hardware,
      )) {
        const hardware = args.botHardware?.[hardwareConfig.id];
        const equipmentId = mapping.hardwareKeyToEquipmentId[hardwareKey];

        if (!equipmentId && hardware?.equipmentType !== 'robot') {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Equipment not assigned for hardware key ${hardwareKey}`,
            fieldId: 'hardwareConfigEquipmentMapping',
          });
        }

        if (
          robot.isPrimary && // For now, don't try to validate secondary equipment as that would require secondary equipment state
          equipmentId &&
          equipment &&
          !equipment.find((e) => e.id === equipmentId)
        ) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            message: `Selected equipment for ${hardwareKey} is not available`,
            fieldId: 'hardwareConfigEquipmentMapping',
          });
        }
      }
    }
  }

  /**
   * Check if the user ID is valid.
   * @param args - The arguments of the step.
   */
  private checkUserId(
    args: Extract<RcStep.Arguments, { argumentKind: 'CreateRecording' }>,
  ): void {
    const { userId } = args;

    if (!userId || String(userId).trim() === '') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Task ID is required.',
        fieldId: 'taskId',
      });
    }
  }

  /**
   * Check if the task ID is valid.
   * @param args - The arguments of the step.
   * @returns True if the task ID is valid, false otherwise.
   */
  private checkTaskId(
    args: Extract<RcStep.Arguments, { argumentKind: 'CreateRecording' }>,
  ): void {
    const { taskId } = args;

    if (!taskId || String(taskId).trim() === '') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Task ID is required.',
        fieldId: 'taskId',
      });
    }
  }
}

export const CreateRecordingValidation: ValidationRule =
  new CreateRecordingRule().toValidationRule();
